import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { Download, Eye, FileText, Mail as MailIcon, Phone as PhoneIcon, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import DocumentPreviewDrawer from '../components/DocumentPreviewDrawer';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundImage: 'none',
  backgroundColor: theme.palette.background.paper
}));

const UI_COLORS = {
  indigo: '#4f46e5',
  sky: '#0ea5e9',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444'
};

const DOC_TIPO = [
  { value: '', label: 'Tutti' },
  { value: 'contratto', label: 'Contratto' },
  { value: 'mandato', label: 'Mandato' },
  { value: 'comunicazione_recesso', label: 'Comunicazione di recesso' },
  { value: 'relazione', label: 'Relazione attività' },
  { value: 'altro', label: 'Altro' }
];

const formatDateIt = (value) => {
  const s = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '-';
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};

const normalizeCustomerLabel = (customer) => {
  if (!customer) return '';
  const label = String(customer.full_name || '').trim();
  if (label) return label;
  if (customer.id) return `Cliente #${customer.id}`;
  return '';
};

const translateTipoDocumento = (value) => {
  const v = String(value || '').trim().toLowerCase();
  const map = {
    contratto: 'Contratto',
    mandato: 'Mandato',
    comunicazione_recesso: 'Comunicazione di recesso',
    relazione: 'Relazione attività',
    altro: 'Altro'
  };
  return map[v] || value || '-';
};

const parseFileExtension = (filename) => {
  const name = String(filename || '').trim();
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  const ext = name.slice(idx + 1).trim();
  if (!ext || ext.length > 8) return '';
  if (!/^[a-z0-9]+$/i.test(ext)) return '';
  return ext.toUpperCase();
};

export default function RecessiDocumenti() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterTipo, setFilterTipo] = useState('');
  const [filterText, setFilterText] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_caricamento');

  const [customerById, setCustomerById] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewRow, setPreviewRow] = useState(null);
  const [previewOpenInNewTabLoading, setPreviewOpenInNewTabLoading] = useState(false);

  const fetchCustomerById = async (id) => {
    const sid = String(id || '').trim();
    if (!sid) return null;
    if (customerById[sid]) return customerById[sid];
    try {
      const res = await api.get(`/gtl-pratiche/customers/by-id/${sid}`);
      const c = res.data || null;
      if (c?.id != null) {
        setCustomerById((prev) => ({ ...prev, [String(c.id)]: c }));
      }
      return c;
    } catch (_) {
      return null;
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await api.get('/recessi/documenti', {
        params: {
          search: filterText || undefined,
          tipo_documento: filterTipo || undefined,
          limit: 500
        }
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setRows([]);
      toast.error(e?.response?.data?.message || 'Errore nel caricamento dei documenti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPage(0);
      fetchRows();
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [filterText, filterTipo]);

  useEffect(() => {
    const ids = Array.from(
      new Set(
        (rows || [])
          .map((r) => r?.id_cliente)
          .filter(Boolean)
          .map((x) => String(x))
      )
    );
    const missing = ids.filter((id) => !customerById[id]);
    if (!missing.length) return undefined;

    let cancelled = false;
    (async () => {
      const chunkSize = 6;
      for (let i = 0; i < missing.length; i += chunkSize) {
        if (cancelled) return;
        const chunk = missing.slice(i, i + chunkSize);
        await Promise.all(chunk.map((id) => fetchCustomerById(id)));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rows]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRows = useMemo(() => {
    const stabilized = (rows || []).map((el, index) => [el, index]);
    const pickSortValue = (row) => {
      if (orderBy === 'nome_file') return String(row?.nome_file || '');
      if (orderBy === 'tipo_documento') return String(row?.tipo_documento || '');
      if (orderBy === 'id_recesso') return Number(row?.id_recesso || 0);
      if (orderBy === 'cliente') {
        const c = row?.id_cliente ? customerById[String(row.id_cliente)] || null : null;
        return String(normalizeCustomerLabel(c) || '');
      }
      if (orderBy === 'data_caricamento') return String(row?.data_caricamento || '');
      return row?.[orderBy];
    };
    const comparator = (a, b) => {
      const va = pickSortValue(a[0]);
      const vb = pickSortValue(b[0]);
      const isNumber = orderBy === 'id_recesso';
      const na = isNumber ? Number(va || 0) : String(va || '');
      const nb = isNumber ? Number(vb || 0) : String(vb || '');
      if (nb < na) return order === 'asc' ? 1 : -1;
      if (nb > na) return order === 'asc' ? -1 : 1;
      return a[1] - b[1];
    };
    stabilized.sort(comparator);
    return stabilized.map((x) => x[0]);
  }, [rows, order, orderBy, customerById]);

  const paginatedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewLoading(false);
    setPreviewUrl('');
    setPreviewRow(null);
  };

  const handleOpenPreview = async (row) => {
    const sid = String(row?.id_documento || '').trim();
    if (!sid) return;
    setPreviewRow(row);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl('');
    try {
      const res = await api.get(`/recessi/documenti/${sid}/preview-url`);
      const url = res.data?.url;
      if (url) {
        setPreviewUrl(url);
      } else {
        toast.error('URL non disponibile');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore apertura documento');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpenPreviewInNewTab = async () => {
    if (previewOpenInNewTabLoading) return;
    const sid = String(previewRow?.id_documento || '').trim();
    if (!sid) return;
    setPreviewOpenInNewTabLoading(true);
    try {
      let url = previewUrl;
      if (!url) {
        const res = await api.get(`/recessi/documenti/${sid}/preview-url`);
        url = res.data?.url || '';
        if (url) setPreviewUrl(url);
      }
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('URL non disponibile');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore apertura documento');
    } finally {
      setPreviewOpenInNewTabLoading(false);
    }
  };

  const openDocumentoUrl = async (docId) => {
    const sid = String(docId || '').trim();
    if (!sid) return;
    try {
      const res = await api.get(`/recessi/documenti/${sid}/download-url`);
      const url = res.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('URL non disponibile');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore download documento');
    }
  };

  const columns = useMemo(
    () => [
      { key: 'nome_file', label: 'Documento', sortable: true, align: 'left' },
      { key: 'tipo_documento', label: 'Tipo', sortable: true, align: 'left' },
      { key: 'cliente', label: 'Cliente', sortable: true, align: 'left' },
      { key: 'id_recesso', label: 'Recesso', sortable: true, align: 'left' },
      { key: 'data_caricamento', label: 'Caricato', sortable: true, align: 'left' }
    ],
    []
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: `${UI_COLORS.indigo}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileText size={20} color={UI_COLORS.indigo} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Documenti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ogni documento appartiene a un recesso specifico — clicca sul recesso per aprire il dettaglio
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledCard variant="outlined">
        <CardContent sx={{ p: 2.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {DOC_TIPO.map((t) => {
                const selected = String(filterTipo || '') === String(t.value || '');
                return (
                  <Chip
                    key={t.value || 'all'}
                    label={t.label}
                    variant="outlined"
                    size="small"
                    onClick={() => setFilterTipo(t.value)}
                    sx={{
                      height: 28,
                      borderRadius: 999,
                      fontWeight: 700,
                      borderColor: selected ? theme.palette.primary.main : 'rgba(145, 158, 171, 0.32)',
                      color: selected ? theme.palette.primary.main : 'text.secondary',
                      bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent'
                    }}
                  />
                );
              })}
            </Box>

            <TextField
              size="small"
              placeholder="Cerca documento o cliente..."
              value={filterText}
              onChange={(e) => setFilterText(String(e.target.value || ''))}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align} sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {col.sortable ? (
                          <TableSortLabel
                            active={orderBy === col.key}
                            direction={orderBy === col.key ? order : 'asc'}
                            onClick={() => handleRequestSort(col.key)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      Azioni
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, fontSize: 13 }}>
                        Nessun dato
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row) => {
                      const customer = row.id_cliente ? customerById[String(row.id_cliente)] || null : null;
                      const customerLabel = normalizeCustomerLabel(customer) || (row.id_cliente ? `#${row.id_cliente}` : '-');
                      const dateOnly = String(row.data_caricamento || '').slice(0, 10);
                      const ext = parseFileExtension(row.nome_file);

                      return (
                        <TableRow
                          key={row.id_documento}
                          hover
                          onClick={() => navigate(`/recessi/${row.id_recesso}`)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ minWidth: 320 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <FileText size={18} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={row.nome_file || ''}>
                                {row.nome_file || '-'}
                              </Typography>
                              {ext ? (
                                <Chip
                                  label={ext}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: 12, height: 22, ml: 0.5 }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : null}
                            </Box>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Chip
                              label={translateTipoDocumento(row.tipo_documento)}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 12, height: 22 }}
                            />
                          </TableCell>

                          <TableCell align="left" sx={{ minWidth: 240 }}>
                            {row.id_cliente ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={customerLabel}>
                                  {customerLabel}
                                </Typography>
                                {customer?.email ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                    <MailIcon size={14} />
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {customer.email}
                                    </Typography>
                                  </Box>
                                ) : null}
                                {customer?.telephone ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                    <PhoneIcon size={14} />
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {customer.telephone}
                                    </Typography>
                                  </Box>
                                ) : null}
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              #{row.id_recesso}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatDateIt(dateOnly)}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                                {row.caricato_da_name || '-'}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenPreview(row);
                              }}
                              title="Preview"
                              disabled={!row.percorso_file}
                            >
                              <Eye size={18} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDocumentoUrl(row.id_documento);
                              }}
                              title="Scarica"
                              disabled={!row.percorso_file}
                            >
                              <Download size={18} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={sortedRows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Righe per pagina"
            />
          </Box>
        </CardContent>
      </StyledCard>

      <DocumentPreviewDrawer
        open={previewOpen}
        onClose={handleClosePreview}
        fileName={previewRow?.nome_file || ''}
        fileUrl={previewUrl}
        loading={previewLoading}
        onOpenInNewTab={handleOpenPreviewInNewTab}
        openInNewTabLoading={previewOpenInNewTabLoading}
        onDownload={
          previewRow?.id_documento
            ? () => {
                openDocumentoUrl(previewRow.id_documento);
              }
            : undefined
        }
        downloadDisabled={!previewRow?.percorso_file}
        details={[
          { label: 'Nome', value: previewRow?.nome_file || '-' },
          { label: 'Tipo', value: translateTipoDocumento(previewRow?.tipo_documento) },
          { label: 'Formato', value: parseFileExtension(previewRow?.nome_file) || '-' },
          { label: 'Recesso', value: previewRow?.id_recesso ? `#${previewRow.id_recesso}` : '-' }
        ]}
      />
    </Box>
  );
}
